import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Dialog, Grid, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, CircularProgress, MenuItem, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, ChevronLeft, ChevronRight, AccountBalance } from '@mui/icons-material';
import { getBanks, createBank, updateBank, deleteBank, getCountries } from '../../services/api';

const defaultForm = { name: '', country: '' };

const Bank = () => {
  const [banks, setBanks] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [batchForm, setBatchForm] = useState({ names: '', country: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filter, setFilter] = useState({ name: '', country: '' });
  const [searchTimeout, setSearchTimeout] = useState(null);
  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  // Fetch countries for dropdown
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await getCountries({ page_size: 1000 });
        setCountries(res.results || []);
      } catch (e) {
        console.error('Error fetching countries:', e);
      }
    };
    fetchCountries();
  }, []);

  const fetchData = useCallback(async (pageArg, rowsArg, filterArg = filter) => {
    setLoading(true);
    const params = {
      page: pageArg + 1,
      page_size: rowsArg,
      name: filterArg.name || undefined,
      country: filterArg.country || undefined,
    };
    try {
      const res = await getBanks(params);
      setBanks(res.results || []);
      setTotal(res.count || res.total || 0);
    } catch (e) {
      console.error('Error fetching banks:', e);
      setBanks([]);
      setTotal(0);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, [page, rowsPerPage, filter, fetchData]);

  // Pagination handlers
  const handlePrevPage = () => { if (page > 0) setPage(page - 1); };
  const handleNextPage = () => { if (page < totalPages - 1) setPage(page + 1); };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => { setCustomRowsInput(e.target.value.replace(/\D/g, '')); };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) { setRowsPerPage(val); setPage(0); }
    setCustomRowsActive(false);
  };
  const handleRefresh = () => { setPage(0); fetchData(0, rowsPerPage); };

  // CRUD handlers
  const handleOpen = (bank = null) => {
    setEditId(bank ? bank.id : null);
    const countryId = bank?.country?.id || bank?.country_id || (typeof bank?.country === 'number' ? bank.country : '');
    setForm(bank ? { name: bank.name, country: countryId } : defaultForm);
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setForm(defaultForm); setEditId(null); };
  
  const handleBatchOpen = () => {
    setBatchForm({ names: '', country: '' });
    setBatchOpen(true);
  };
  const handleBatchClose = () => { setBatchOpen(false); setBatchForm({ names: '', country: '' }); };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setSnackbar({ open: true, message: 'Bank name is required', severity: 'error' });
      return;
    }
    if (!form.country) {
      setSnackbar({ open: true, message: 'Country is required', severity: 'error' });
      return;
    }
    try {
      if (editId) {
        await updateBank(editId, form);
        setSnackbar({ open: true, message: 'Bank updated successfully', severity: 'success' });
      } else {
        await createBank(form);
        setSnackbar({ open: true, message: 'Bank added successfully', severity: 'success' });
      }
      handleClose();
      fetchData(page, rowsPerPage);
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Error saving bank', severity: 'error' });
    }
  };

  const handleBatchSave = async () => {
    if (!batchForm.names.trim()) {
      setSnackbar({ open: true, message: 'Bank names are required', severity: 'error' });
      return;
    }
    if (!batchForm.country) {
      setSnackbar({ open: true, message: 'Country is required', severity: 'error' });
      return;
    }

    // Split by comma and trim each name
    const bankNames = batchForm.names
      .split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    if (bankNames.length === 0) {
      setSnackbar({ open: true, message: 'Please enter at least one bank name', severity: 'error' });
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const name of bankNames) {
        try {
          await createBank({ name, country: batchForm.country });
          successCount++;
        } catch (e) {
          errorCount++;
          errors.push(`${name}: ${e.response?.data?.detail || 'Error'}`);
        }
      }

      if (successCount > 0) {
        setSnackbar({ 
          open: true, 
          message: `Successfully added ${successCount} bank(s)${errorCount > 0 ? `. ${errorCount} failed.` : ''}`, 
          severity: errorCount > 0 ? 'warning' : 'success' 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: `Failed to add banks. ${errors.join('; ')}`, 
          severity: 'error' 
        });
      }

      handleBatchClose();
      fetchData(page, rowsPerPage);
    } catch (e) {
      setSnackbar({ open: true, message: 'Error adding banks', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteBank(id);
      setSnackbar({ open: true, message: 'Bank deleted successfully', severity: 'success' });
      setDeleteModal({ open: false, id: null });
      fetchData(page, rowsPerPage);
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Error deleting bank', severity: 'error' });
    }
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 60,
      minWidth: 50,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
    },
    { 
      field: 'name', 
      headerName: 'Bank Name', 
      flex: 1,
      minWidth: 150,
    },
    { 
      field: 'country', 
      headerName: 'Country', 
      width: 150,
      minWidth: 120,
      renderCell: (params) => {
        const countryName = params.row.country?.name || params.row.country_name || '-';
        return countryName !== '-' ? (
          <Chip 
            label={countryName} 
            size="small" 
            sx={{ 
              backgroundColor: '#DC143C', 
              color: 'white',
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: 20, sm: 24 },
              '& .MuiChip-label': {
                px: { xs: 0.5, sm: 1 }
              }
            }}
          />
        ) : '-';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      minWidth: 90,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
          <IconButton 
            onClick={() => handleOpen(params.row)}
            size="small"
            sx={{ 
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }
            }}
          >
            <Edit />
          </IconButton>
          <IconButton 
            color="error" 
            onClick={() => setDeleteModal({ open: true, id: params.row.id })}
            size="small"
            sx={{ 
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }
            }}
          >
            <Delete />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <AccountBalance sx={{ color: '#DC143C', fontSize: { xs: '2rem', sm: '2.5rem' } }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#DC143C',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            Bank Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{ 
              backgroundColor: '#DC143C', 
              '&:hover': { backgroundColor: '#B91C3C' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 }
            }}
          >
            Add Bank
          </Button>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleBatchOpen}
            sx={{ 
              borderColor: '#DC143C', 
              color: '#DC143C', 
              '&:hover': { borderColor: '#B91C3C', backgroundColor: '#FFF0F3' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 }
            }}
          >
            Batch Add
          </Button>
          <IconButton 
            onClick={handleRefresh}
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <Refresh />
          </IconButton>
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Search Bank Name"
            value={filter.name}
            onChange={e => {
              const val = e.target.value;
              setFilter(f => ({ ...f, name: val }));
              if (searchTimeout) clearTimeout(searchTimeout);
              setSearchTimeout(setTimeout(() => fetchData(0, rowsPerPage, { ...filter, name: val }), 500));
              setPage(0);
            }}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="Filter by Country"
            value={filter.country}
            onChange={e => {
              const val = e.target.value;
              setFilter(f => ({ ...f, country: val }));
              if (searchTimeout) clearTimeout(searchTimeout);
              setSearchTimeout(setTimeout(() => fetchData(0, rowsPerPage, { ...filter, country: val }), 500));
              setPage(0);
            }}
            select
            size="small"
            fullWidth
          >
            <MenuItem value="">All Countries</MenuItem>
            {countries.map((country) => (
              <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      {/* Banks Table */}
      <Box sx={{
        width: '100%',
        overflowX: 'auto',
        minHeight: { xs: 400, sm: 520 },
        '&::-webkit-scrollbar': {
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}>
        <DataGrid
          rows={addSerialNumbers(banks, page, rowsPerPage)}
          columns={columns}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          pageSize={rowsPerPage}
          onPageSizeChange={(newPageSize) => { setRowsPerPage(newPageSize); setPage(0); }}
          pagination
          rowCount={total}
          paginationMode="server"
          rowsPerPageOptions={[10, 20, 50, 100]}
          disableSelectionOnClick
          loading={loading}
          hideFooter
          sx={{
            border: 'none',
            width: '100%',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#DC143C',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
            },
            '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              color: 'white'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#FFF0F3'
            },
            '& .MuiDataGrid-cell': {
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              padding: { xs: '4px 8px', sm: '8px 12px' }
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none'
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none'
            }
          }}
        />
      </Box>
      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'space-between' }, 
        mt: 3, 
        gap: { xs: 1, sm: 2 },
        flexWrap: 'wrap',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={handlePrevPage} 
            disabled={page === 0}
            size="small"
            sx={{ 
              '&:disabled': { opacity: 0.5 }
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography 
            variant="body2"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton 
            onClick={handleNextPage} 
            disabled={page >= totalPages - 1}
            size="small"
            sx={{ 
              '&:disabled': { opacity: 0.5 }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body2"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ 
                width: { xs: 80, sm: 100 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ 
                width: { xs: 80, sm: 100 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>
      {/* Add/Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          {editId ? 'Edit Bank' : 'Add Bank'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Bank Name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            fullWidth
            sx={{ mt: 2 }}
            size="small"
          />
          <TextField
            label="Country"
            value={form.country}
            onChange={e => setForm({ ...form, country: e.target.value })}
            select
            fullWidth
            sx={{ mt: 2 }}
            size="small"
          >
            {countries.map((country) => (
              <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={handleClose}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              backgroundColor: '#DC143C',
              '&:hover': { backgroundColor: '#B91C3C' }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Batch Add Dialog */}
      <Dialog 
        open={batchOpen} 
        onClose={handleBatchClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Batch Add Banks
        </DialogTitle>
        <DialogContent>
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            Enter bank names separated by commas. Each name will be added as a separate bank.
          </Typography>
          <TextField
            label="Bank Names (comma-separated)"
            value={batchForm.names}
            onChange={e => setBatchForm({ ...batchForm, names: e.target.value })}
            fullWidth
            multiline
            rows={4}
            placeholder="Bank 1, Bank 2, Bank 3"
            sx={{ mt: 2 }}
            size="small"
          />
          <TextField
            label="Country"
            value={batchForm.country}
            onChange={e => setBatchForm({ ...batchForm, country: e.target.value })}
            select
            fullWidth
            sx={{ mt: 2 }}
            size="small"
          >
            {countries.map((country) => (
              <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={handleBatchClose}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBatchSave} 
            variant="contained"
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              backgroundColor: '#DC143C',
              '&:hover': { backgroundColor: '#B91C3C' }
            }}
          >
            Add Banks
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteModal.open} 
        onClose={() => setDeleteModal({ open: false, id: null })}
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Delete Bank
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Are you sure you want to delete this bank?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setDeleteModal({ open: false, id: null })}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleDelete(deleteModal.id)} 
            color="error" 
            variant="contained"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Bank;

